import {ChipType, Colors} from "@bounce/shared/ui";
import {ConditionGroup, SegmentCategory} from "../../../apis/requests/segments/types";

export const OPERATOR_LABELS: Record<string, string> = {
  equals: "=",
  is_not: "!=",
  in: "in",
  not_in: "not in",
  greater_than: ">",
  less_than: "<",
  greater_than_or_equal: ">=",
  less_than_or_equal: "<=",
  equal_to: "=",
  is_true: "is true",
  is_false: "is false",
};

export const MULTI_VALUE_OPERATORS = new Set(["in", "not_in"]);
export const NO_VALUE_OPERATORS = new Set(["is_true", "is_false"]);

export const CURRENCY_PARAMETERS = new Set(["balance", "installment_amount"]);
export const FRACTION_PARAMETERS = new Set(["one_time_discount", "long_plan_discount"]);

export const FEATURE_FLAG_PARAMETER = "feature_flag";
export const PRODUCT_PARAMETER = "product";
export const CURRENT_CREDITOR_PARAMETER = "current_creditor";

export const MARKETING_SEGMENT_TYPES = ["GENERAL", "EXCLUSIVE"] as const;
export const VALIDATION_SEGMENT_TYPE = "VALIDATION";
export const SEGMENT_TYPES = [...MARKETING_SEGMENT_TYPES, VALIDATION_SEGMENT_TYPE] as const;
export const DEFAULT_SEGMENT_TYPE = "GENERAL";
export const SEGMENT_TYPE_INFO: Record<(typeof SEGMENT_TYPES)[number], {label: string; description: string}> = {
  GENERAL: {
    label: "General",
    description: "Can be combined with other general segments into audiences.",
  },
  EXCLUSIVE: {
    label: "Exclusive",
    description:
      "Always ranked above general segments. Matching accounts belong to this segment only, and no other segment is evaluated for them.",
  },
  VALIDATION: {
    label: "Validation",
    description:
      "Used for validation only and kept apart from audiences. Each account gets the highest-priority validation segment it matches.",
  },
};

export const SEGMENT_CATEGORIES = [
  "credit_reporting",
  "discount_payment_plan",
  "vertical",
] as const satisfies readonly SegmentCategory[];
export const NO_SEGMENT_CATEGORY = "";
export const OPTIONAL_PLACEHOLDER = "Optional";
export const CLEAR_OPTION_LABEL = "None";
export const SEGMENT_CATEGORY_INFO: Record<
  SegmentCategory,
  {label: string; bucketsHint: string; chipVariant: ChipType["variant_"]; color: string; rowBackground: string}
> = {
  credit_reporting: {
    label: "Credit reporting",
    bucketsHint: "Before, Eligible – in validation, Eligible – reportable, After, No",
    chipVariant: "blue",
    color: Colors.blue._500,
    rowBackground: Colors.blue._100,
  },
  discount_payment_plan: {
    label: "Discount & payment plan",
    bucketsHint: "Discount yes/no × payment plan yes/no",
    chipVariant: "success",
    color: Colors.green._700,
    rowBackground: Colors.green._100,
  },
  vertical: {
    label: "Vertical",
    bucketsHint: "One bucket per vertical",
    chipVariant: "info",
    color: Colors.secondary._700,
    rowBackground: Colors.secondary._100,
  },
};
export const SEGMENT_CATEGORY_DESCRIPTION = "Buckets in a category are mutually exclusive and share one priority.";
export const EVERYONE_ELSE_LABEL = "Everyone else";
export const EMPTY_FALLBACK_NOTE = "No accounts right now: the other buckets cover everyone.";
export const EMPTY_BUCKET_NOTE = "No accounts right now.";
export const CATEGORY_FALLBACK_CONDITIONS_LABEL = "Everyone not in another bucket";
export const CATEGORY_FALLBACK_DESCRIPTION =
  "This bucket holds every account that matches none of the other buckets, so the category covers everyone. It has no conditions and is added and removed automatically.";
export const CATEGORY_FALLBACK_PENDING_HINT = `An "${EVERYONE_ELSE_LABEL}" bucket for accounts that match no other bucket is added when you save.`;
export const EMPTY_CONDITIONS: ConditionGroup = {match_mode: "all", rules: []};
